import { AB_TEST_CONFIG, logger } from './const';
import { VolcanoSDK } from './volcano';

// --- Types ---
export type ABTestValue =
  | string
  | number
  | boolean
  | null
  | { [key: string]: ABTestValue }
  | ABTestValue[];

export type ABTestExperiments = Record<string, ABTestValue>;

/**
 * 实验类型注册表。接入方通过模块增强声明实验值类型，调用 `getExperiment` 时即可自动推导。
 *
 * @example
 * declare module '@wegic/abtest' {
 *   interface ABTestExperimentMap {
 *     generatePay2: 'GroupA' | 'GroupB';
 *   }
 * }
 */
export interface ABTestExperimentMap {}

type ABTestKnownKey = keyof ABTestExperimentMap & string;

export interface ABTestExposureEvent {
  /** 实验 key */
  name: string;
  /** 命中的分组值 */
  value: ABTestValue;
  /** 实验来源 */
  source: 'client' | 'server';
  /** 火山 vid（仅 client 来源可能存在） */
  vid?: string;
}

export interface ABTestConfig {
  /** 用户 ID，传入后拉取服务端实验 */
  userId?: string;
  /** 是否启用火山客户端 SDK，默认 false */
  enableClientSDK?: boolean;
  /** 服务端实验接口 base，默认 /api/user/v1 */
  baseUrl?: string;
  /** 火山 SDK appId */
  appId?: number;
  /** 曝光回调 */
  onExpose?: (event: ABTestExposureEvent) => void;
}

export type ABTestListener = () => void;

// --- Main Class ---

export class ABTestSDKClient {
  private experiments: ABTestExperiments = {};
  private clientExperiments: ABTestExperiments = {};
  private serverExperiments: ABTestExperiments = {};
  private clientExperimentVids: Record<string, string> = {};
  private abVersion: string = '';
  private listeners = new Set<ABTestListener>();

  /** 火山客户端 SDK 实例，init 启用 enableClientSDK 后可用，供调试 */
  volcanoSDK: VolcanoSDK | null = null;

  private currentConfig: ABTestConfig = {};
  private initToken = 0;

  async init(config: ABTestConfig = {}): Promise<void> {
    const token = ++this.initToken;
    this.currentConfig = config;
    const baseUrl = config.baseUrl ?? '/api/user/v1';
    const appId = config.appId ?? AB_TEST_CONFIG.appId;
    const userId = config.userId;

    const [client, server] = await Promise.all([
      config.enableClientSDK ? this.initClientSDK(appId, userId) : Promise.resolve({}),
      userId ? this.fetchServerExperiments(baseUrl, userId) : Promise.resolve({}),
    ]);

    // 期间又有新的 init 调用，丢弃本次结果，避免旧请求覆盖新状态
    if (token !== this.initToken) {
      logger.debug('AB实验初始化结果过期，已忽略', { staleToken: token, currentToken: this.initToken });
      return;
    }

    this.clientExperiments = client;
    this.serverExperiments = server;
    this.experiments = { ...server, ...client };

    logger.info('AB实验初始化完成', this.experiments);
    this.notify();
  }

  getExperiment<K extends ABTestKnownKey>(name: K): ABTestExperimentMap[K] | null;
  getExperiment<K extends ABTestKnownKey>(name: K, defaultValue: ABTestExperimentMap[K]): ABTestExperimentMap[K];
  getExperiment<T = ABTestValue>(name: string, defaultValue?: T): T;
  getExperiment(name: string, defaultValue?: unknown): unknown {
    return this.experiments[name] ?? defaultValue ?? null;
  }

  getConfig(): ABTestConfig {
    return this.currentConfig;
  }

  /** 手动更新实验值（调试用途或本地覆盖） */
  setExperiment(name: string, value: ABTestValue): void {
    this.experiments[name] = value;
    this.notify();
  }

  /**
   * 触发实验曝光。仅当 `name` 命中一个真实分组（非 null/undefined）时上报。
   * - 客户端实验：会调用火山 SDK 的 `getVar` 触发原生曝光
   * - 同时调用 `config.onExpose`，由宿主上报 `abtest_exposure` 埋点
   */
  expose(name: string): void {
    const value = this.experiments[name];
    if (value === undefined || value === null) {
      return;
    }

    const source: 'client' | 'server' = name in this.clientExperiments ? 'client' : 'server';

    if (source === 'client') {
      this.volcanoSDK?.expose(name);
    }

    const vid = this.clientExperimentVids[name];
    this.currentConfig.onExpose?.({
      name,
      value,
      source,
      ...(vid ? { vid } : {}),
    });
  }

  /** 订阅实验值变更，返回取消订阅函数 */
  subscribe(listener: ABTestListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  getAllExperiments(): ABTestExperiments {
    return this.experiments;
  }

  getVids(): Record<string, string> {
    return this.clientExperimentVids;
  }

  /** 返回每个实验 key 的来源：'client' | 'server' */
  getSources(): Record<string, 'client' | 'server'> {
    const sources: Record<string, 'client' | 'server'> = {};
    for (const key of Object.keys(this.serverExperiments)) {
      sources[key] = 'server';
    }
    // client 优先级高于 server（合并时 client 覆盖 server），所以后写
    for (const key of Object.keys(this.clientExperiments)) {
      sources[key] = 'client';
    }
    return sources;
  }

  // --- Private: Server ---

  private async fetchServerExperiments(baseUrl: string, userId: string): Promise<ABTestExperiments> {
    try {
      const res = await fetch(`${baseUrl}/abtest/${userId}`, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      const records: Array<{ abTestBusinessKey?: string; groupName?: string }> = json?.data ?? [];
      return records.reduce<ABTestExperiments>((result, item) => {
        if (item.abTestBusinessKey) {
          result[item.abTestBusinessKey] = item.groupName || null;
        }
        return result;
      }, {});
    } catch (error) {
      logger.error('后端实验获取失败', error);
      return {};
    }
  }

  // --- Private: Volcano Client SDK ---

  private async initClientSDK(appId: number, userId?: string): Promise<ABTestExperiments> {
    try {
      const volcano = new VolcanoSDK({ timeout: AB_TEST_CONFIG.sdkTimeout });
      await volcano.init({ appId, channel: AB_TEST_CONFIG.channel, userId });
      const snapshot = await volcano.getAllVars();

      this.volcanoSDK = volcano;
      this.clientExperimentVids = snapshot.vids;
      this.abVersion = snapshot.abVersion;
      return snapshot.experiments;
    } catch (error) {
      logger.error('客户端 AB SDK 初始化失败', error);
      return {};
    }
  }
}

const ABTestSDK = new ABTestSDKClient();
export default ABTestSDK;
